import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Auth } from 'aws-amplify';
import { AccountStatus } from '../enums';
import { UserOutlined, CaretDownOutlined } from '@ant-design/icons';
import {
  Layout as AntdLayout,
  Avatar,
  Dropdown,
  Tooltip,
  MenuProps,
  Button,
  ConfigProvider,
  message,
  Modal,
} from 'antd';

import {
  setAvatar,
  setEditingCardProps,
  setEditingChapterId,
  setUpgradeModalOpen,
} from '../actions';
import { IState, IUser, Giide } from '../graphql/types';
import {
  BASE_URL,
  getSignedUrl,
  publishClicked,
  handleInputChange,
  redirectToLogin,
  State,
  previewClicked,
  haveGiideChapters,
} from './utils';

import styles from './Components.module.css';

import usePreview from '../hooks/usePreview';
import useGiideContentModal from '../hooks/useGiideContentModal';
import { FocusInput } from './Focused';
import useGiideOperations from '../hooks/useGiideOperations';
import LoadingIcon from './LoadingIcon';
import { Dispatch } from 'redux';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { yellowButtonTheme } from '../theme';

function getUserProfileImage(
  user: IUser,
  dispatch: Dispatch<any>,
  service?: string
) {
  let fileKey = `public/${user.tenantID}/${user?.userId}`;

  if (service === 'cloudinary') {
    if (!user?.profileImageId) {
      return;
    }

    fileKey = `studio/${user.tenantID}/${user.userId}/avatar/${user?.profileImageId}`;
  }

  getSignedUrl(fileKey, undefined, undefined, undefined, service)
    .then(data => {
      fetch(data?.url as string).then(res => {
        if (res.ok) {
          res
            .blob()
            .then(blob => dispatch(setAvatar(URL.createObjectURL(blob))));
        }
      });
    })
    .catch(console.log);
}

const Header: React.FC<{
  authState: string | undefined;
  onStateChange?: (authState: string, data?: any) => void;
  user?: IUser;
}> = ({ authState, onStateChange, user }) => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const avatar = useSelector<IState, string | undefined>(state => state.avatar);
  const giide = useSelector<IState, Giide | undefined>(state => state.giide);
  const hasChapters = haveGiideChapters(giide, user);
  const { loadPreview, previewLoading, previewModal } = usePreview();

  const accountDisabled = useSelector<IState, boolean>(
    state => state.accountDisabled
  );

  const {
    GiideContentModal,
    setModalContent,
    ModalContent,
    tooltipMessagePreviewAndPublish,
    tooltipMessageShareLink,
    tooltipMessageUnpublish,
    tooltipMessageSettingsAndDelete,
  } = useGiideContentModal();
  const [giideTitle, setGiideTitle] = useState<string>(giide?.title ?? '');
  const {
    updateGiideTitle,
    onDeleteGiide,
    onUnpublishGiide,
    giideTitleEditing,
    setGiideTitleEditing,
  } = useGiideOperations(giide?.id);
  const fullName = user?.fullName || '';

  const handlePreview = useCallback(async () => {
    await previewClicked(loadPreview);
    const previewElem = document.getElementById('Preview');
    if (previewElem) previewElem.classList.remove('ant-menu-item-active');
  }, [loadPreview]);

  const handleGiideTitleSave = useCallback(() => {
    updateGiideTitle(giideTitle.length > 0 ? giideTitle : giide?.title, true);
  }, [giide, giideTitle, updateGiideTitle]);

  useEffect(() => {
    setGiideTitle(giide?.title ?? '');
  }, [giide?.title]);

  useEffect(() => {
    // TODO: This runs four times, it's unnecessary
    if (!avatar && user?.userId) {
      const userHasProfileImage =
        user?.profileImageName && user?.profileImageName.length > 0;

      if (userHasProfileImage) {
        const imageServiceExists =
          typeof user?.profileImageService === 'string' &&
          user?.profileImageService.length > 1;

        const imageService = imageServiceExists
          ? user?.profileImageService
          : undefined;

        getUserProfileImage(user, dispatch, imageService);
      }
    }
  }, [user, dispatch, avatar]);

  const isDashboardView = React.useMemo(() => {
    return pathname === '/';
  }, [pathname]);

  const isAccountView = React.useMemo(() => {
    return pathname.includes('/account');
  }, [pathname]);

  const AccountStatusMessage = () => {
    if (
      user?.plan &&
      user?.plan.accountStatus === AccountStatus.freeTrialExpired
    ) {
      return (
        <span className={styles.freeTrialStatus}>
          Your free trial <b>has ended</b>
        </span>
      );
    }

    if (
      user?.plan &&
      (user?.plan.accountStatus === AccountStatus.paddleCanceled ||
        user?.plan.accountStatus === AccountStatus.paddleDeleted)
    ) {
      return (
        <span className={styles.freeTrialStatus}>
          Subscription <b>canceled</b>
        </span>
      );
    }

    return !user?.plan?.paddlePlanID && user?.plan?.freeTrialEndDate ? (
      <span className={styles.freeTrialStatus}>
        Your free trial{' '}
        <b>
          will expire on{' '}
          {new Date(user?.plan.freeTrialEndDate).toLocaleDateString()}
        </b>
      </span>
    ) : null;
  };

  const giideUrl = useMemo<string>(
    () => `https://${process.env.REACT_APP_PLAYER_URL}/giide/${giide?.id}`,
    [giide?.id]
  );

  const [messageAPI, messageContextHolder] = message.useMessage();
  const [modalAPI, modalContextHolder] = Modal.useModal();

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(giideUrl);
    messageAPI.success('Copied to clipboard!');
  }, [giideUrl, messageAPI]);

  const giideEditingDropdownMenuItems: MenuProps['items'] = [
    {
      key: 'Preview',
      disabled: !!tooltipMessagePreviewAndPublish,
      label: (
        <div id="Preview" onClick={handlePreview}>
          <Tooltip
            title={tooltipMessagePreviewAndPublish || ''}
            placement={'left'}
          >
            Preview
            {previewLoading && <LoadingIcon />}
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'Preview and Share',
      disabled: !!tooltipMessagePreviewAndPublish,
      label: (
        <div
          onClick={() =>
            publishClicked(
              setModalContent,
              ModalContent,
              dispatch,
              giide,
              true,
              modalAPI
            )
          }
        >
          <Tooltip
            title={tooltipMessagePreviewAndPublish || ''}
            placement={'left'}
          >
            Publish
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'Share link',
      disabled: !!tooltipMessageShareLink,
      label: (
        <div
          onClick={() => {
            copyToClipboard();
            dispatch(setEditingChapterId(undefined));
          }}
        >
          <Tooltip title={tooltipMessageShareLink || ''} placement={'left'}>
            Get Share Link
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'Unpublish',
      disabled: !!tooltipMessageUnpublish,
      danger: true,
      label: (
        <div
          onClick={() => {
            onUnpublishGiide(true);
            dispatch(setEditingChapterId(undefined));
          }}
        >
          <Tooltip title={tooltipMessageUnpublish || ''} placement={'left'}>
            Unpublish
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'Delete',
      disabled: !!tooltipMessageSettingsAndDelete,
      danger: true,
      label: (
        <div
          onClick={() => {
            onDeleteGiide(true);
            dispatch(setEditingChapterId(undefined));
          }}
        >
          <Tooltip
            title={tooltipMessageSettingsAndDelete || ''}
            placement={'left'}
          >
            Delete
          </Tooltip>
        </div>
      ),
    },
  ];

  const accountDropdownMenuItems: MenuProps['items'] = [
    {
      key: 'name',
      label: <div>{fullName}</div>,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'Account',
      label: <Link to={'/account'}>Account</Link>,
    },
    {
      key: 'Help Center',
      label: (
        <a href={`${BASE_URL}/help`} target="_blank" rel="noopener noreferrer">
          Help Center
        </a>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'Privacy Policy',
      label: (
        <a
          href={`${BASE_URL}/privacy-policy`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      ),
    },
    {
      key: 'Terms of Use',
      label: (
        <a
          href={`${BASE_URL}/terms-of-use`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>
      ),
    },
    {
      key: 'Creator terms of use',
      label: (
        <a
          href={`${BASE_URL}/creator-terms-of-use`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Creator Terms of Use
        </a>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'Logout',
      label: <div onClick={() => Auth.signOut()}>Log Out</div>,
    },
  ];

  return (
    <ConfigProvider>
      {modalContextHolder}
      {messageContextHolder}
      <AntdLayout.Header className={styles.layoutHeader}>
        <div className={styles.headerLeft}>
          <div
            style={{
              cursor: 'pointer',
              width: '72px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => {
              dispatch(setEditingCardProps());
              if (authState !== 'signedIn') {
                onStateChange && onStateChange('signIn', {});
                redirectToLogin(push);
              } else {
                push('/');
              }
            }}
          >
            <Logo noTransition={true} />
          </div>
        </div>
        <div
          className={`${styles.headerCenter} ${
            !hasChapters ? styles.headerCenterSingleChapter : ''
          }`}
        >
          {isDashboardView
            ? null
            : giide &&
              giide?.id &&
              user?.tenantID &&
              user.userId &&
              !isAccountView && (
                <div className={styles.headerGiideDropdown}>
                  <span className={styles.statusWrapper}>
                    <span className={styles.statusType}>
                      {
                        State[
                          giide?.publishState === 'published'
                            ? 'published'
                            : 'unpublished'
                        ]
                      }
                    </span>
                    <span className={styles.statusDot}>•</span>
                  </span>
                  {giide && giide.title && !isAccountView && (
                    <>
                      <span
                        id="giideHeaderTitle"
                        className={`${
                          giideTitleEditing ? styles.giideHeaderTitleHiiden : ''
                        } ${styles.giideHeaderTitle}`}
                        onClick={() => setGiideTitleEditing(true)}
                      >
                        {giide.title}
                      </span>
                      {giideTitleEditing && (
                        <FocusInput
                          onChange={handleInputChange(setGiideTitle)}
                          onPressEnter={handleGiideTitleSave}
                          onBlur={handleGiideTitleSave}
                          defaultValue={giide.title}
                          maxLength={40}
                          style={{
                            width: `${
                              (document.getElementById('giideHeaderTitle')
                                ?.clientWidth || 0) + 20
                            }px`,
                          }}
                        />
                      )}
                    </>
                  )}
                  <Dropdown
                    // className={styles.headerGiideTitleDropdown}
                    placement="bottomRight"
                    trigger={['click']}
                    menu={{ items: giideEditingDropdownMenuItems }}
                  >
                    <Button
                      type={'link'}
                      style={{
                        paddingLeft: '3px',
                        paddingRight: '3px',
                      }}
                      onClick={e => e.preventDefault()}
                    >
                      {giideTitleEditing ? null : <CaretDownOutlined />}
                    </Button>
                  </Dropdown>
                </div>
              )}
        </div>
        <div className={styles.headerRight}>
          {authState === 'signedIn' && (
            <div className={styles.headerRightAccountButtons}>
              {/* FOR PADDLE UPGRADING */}
              <AccountStatusMessage />

              {user &&
                user.plan &&
                // IF (not contact us plan, then show the button)
                (user.plan.paddlePlanID ===
                  +process.env.REACT_APP_STARTER_PLAN_ID! ||
                  user.plan.paddlePlanID ===
                    +process.env.REACT_APP_PREMIUM_PLAN_ID! ||
                  user.plan.accountStatus === AccountStatus.freeTrialActive ||
                  accountDisabled) && (
                  <ConfigProvider theme={yellowButtonTheme}>
                    <Button
                      type={'primary'}
                      onClick={() => dispatch(setUpgradeModalOpen(true))}
                    >
                      Upgrade
                    </Button>
                  </ConfigProvider>
                )}

              <Dropdown
                // onOpenChange={setAccountDropdownVisible}
                // open={accountDropdownVisible}
                placement="bottomRight"
                trigger={['click']}
                menu={{ items: accountDropdownMenuItems }}
              >
                <Avatar
                  icon={<UserOutlined />}
                  src={avatar}
                  style={{ cursor: 'pointer', border: '1px solid #DDDDDD' }}
                />
              </Dropdown>
            </div>
          )}
        </div>

        {previewModal()}
        <GiideContentModal />
      </AntdLayout.Header>
    </ConfigProvider>
  );
};

export default Header;
